/**
 * 主题 store：'light' | 'dark' | 'system'（默认跟随系统）。
 * 持久化到 localStorage('lc-theme')，index.html 内联脚本首帧防闪。
 */
import { create } from 'zustand';
import { STORAGE_KEYS } from '../lib/utils';

export type ThemeMode = 'light' | 'dark' | 'system';

function isSystemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveDark(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && isSystemDark());
}

/** 把 dark 类应用到 <html>（darkMode: 'class'） */
export function applyTheme(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', resolveDark(mode));
}

function readInitialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.theme);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    /* localStorage 不可用时退回 system */
  }
  return 'system';
}

interface ThemeState {
  mode: ThemeMode;
  /** 当前是否暗色（跟随系统时实时解析） */
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  /** 明暗切换：system 视当前解析结果，直接切到相反的确定态 */
  toggle: () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  mode: readInitialMode(),
  isDark: false,
  setMode(mode) {
    try {
      localStorage.setItem(STORAGE_KEYS.theme, mode);
    } catch {
      /* 忽略持久化失败 */
    }
    applyTheme(mode);
    set({ mode, isDark: resolveDark(mode) });
  },
  toggle() {
    const next: ThemeMode = resolveDark(get().mode) ? 'light' : 'dark';
    get().setMode(next);
  },
}));

// 初始化：应用当前主题 + 监听系统主题变化（mode === 'system' 时实时跟随）
applyTheme(useTheme.getState().mode);
useTheme.setState({ isDark: resolveDark(useTheme.getState().mode) });
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  const { mode } = useTheme.getState();
  if (mode === 'system') {
    applyTheme(mode);
    useTheme.setState({ isDark: resolveDark(mode) });
  }
});
